import { ElementNode, Text, View, Show, hexColor, IntrinsicTextNodeStyleProps } from "@lightningtv/solid";
import { Button, ButtonContainer, Column, Row } from "@lightningtv/solid-ui";
import { useParams, useNavigate, useLocation } from "@solidjs/router";
import {
  createEffect,
  createResource,
  createSignal,
  createSelector,
  //@ts-ignore
  For,
  createMemo,
} from "solid-js";
import { setIsPlaying } from "../../state";
import styles from "../styles";
import * as provider from "../api/providers/entity";
import type { Tile } from "../api/formatters/ItemFormatter";
import videoPlayer from "../video";
import { createInfiniteScroll } from "../components/pagination";
import episodesProvider from "../api/providers/episodesProvider";
import ContentBlockSeries from "../components/ContentBlockSeries";
import LoadingIndicator from "./loading/loading";

// ✅ Import CORS handler
import { getCORSSafeImageUrl, createCORSSafeImage } from "../components/ImageCORSHandler";

const RowStyles: any = {
  display: "flex",
  justifyContent: "flexStart",
  width: 300,
  height: 300,
  color: hexColor("00000000"),
  gap: 26,
  y: -110,
} satisfies IntrinsicTextNodeStyleProps;

const columnY = 640;

const Backdrop = {
  color: hexColor("#000000"),
  alpha: 0,
  width: 1900,
  height: 890,
  x: -160,
  y: columnY,
  borderRadius: 30,
};

const SerieDetails = () => {
  const params = useParams();

  let backdropRef: any, seriesRow: any;
  let myButton: any;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let episodesViewRef: any;

  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [isNumberChanging, setIsNumberChanging] = createSignal(false);
  const [number, setNumber] = createSignal(null);
  const [index, setIndex] = createSignal(0);
  const [backImage, setBackImage] = createSignal<string | null>(null);
  const [backImageLoaded, setBackImageLoaded] = createSignal(false); // ✅ Track if image loaded successfully
  const [backImageError, setBackImageError] = createSignal(false); // ✅ Track if image failed to load
  const [isEpsiodesView, setisEpisodesView] = createSignal(false);
  const [episodeTitle, setEpsiodeTitle] = createSignal('');
  const [epsisodeRating, setEpsiodeRating] = createSignal('');
  const [epsisodeDuration, setEpsiodeDuration] = createSignal('');
  const [epsisodeDate, setEpsiodeDate] = createSignal('');
  const stored = JSON.parse(localStorage.getItem("seriesCategoryIDs")) || [];
  const [lockedCategoryIDs, setLockedCategoryIDs] = createSignal(stored);

  const [data] = createResource(() => ({ ...params }), provider.getInfo);

  // ✅ Enhanced CORS-safe background image handling with error tracking
  createEffect(async () => {
    const currentData = data();

    if (currentData?.backgroundImage()) {
      const rawUrl =
        currentData.backgroundImage?.()[0] ||
        currentData.heroContent?.coverImage?.() ||
        currentData.heroContent?.posterImage?.() ||
        null;

      console.log('currentData', currentData.heroContent?.coverImage?.());

      if (rawUrl) {
        try {
          setBackImageLoaded(false);
          setBackImageError(false);

          const safeUrl = await getCORSSafeImageUrl(rawUrl, "/assets/placeholder.png");

          // Test if the image actually loads
          const img = new Image();
          img.onload = () => {
            setBackImage(safeUrl);
            setBackImageLoaded(true);
            setBackImageError(false);
          };
          img.onerror = () => {
            console.error('Background image failed to load:', safeUrl);
            setBackImage(null);
            setBackImageLoaded(false);
            setBackImageError(true);
          };
          img.src = safeUrl;

        } catch (error) {
          console.error('Error processing background image:', error);
          setBackImage(null);
          setBackImageLoaded(false);
          setBackImageError(true);
        }
      } else {
        setBackImage(null);
        setBackImageLoaded(false);
        setBackImageError(false);
      }
    } else {
      setBackImage(null);
      setBackImageLoaded(false);
      setBackImageError(false);
    }
  });

  // ✅ Computed signal for whether background should be transparent or colored
  const shouldUseTransparentBackground = () => backImage() && backImageLoaded() && !backImageError();

  const [seasons] = createResource<any, Tile[]>(() => ({ ...params }), provider.getSeriesSeasons);

  const providerOfSeries = createMemo(() => {
    //@ts-ignore
    return createInfiniteScroll(episodesProvider(params.filter || "all", number(), index()));
  });

  const isFirst = createSelector(() => 0);

  const onEscape = () => {
    if (isVideoPlaying()) {
      videoPlayer.destroy().catch(console.error);
      setIsVideoPlaying(false);
    } else {
      document.getElementsByTagName("canvas")[0].focus();
      setTimeout(() => navigate("/series"), 100);
      setIsPlaying(true);
    }
  };

  const onEnterSeries = (idSeries: any, title: any, container_extension: any) => {
    navigate(
      `/player?str=${idSeries}&type=series&name=${encodeURIComponent(title)}&extc=${container_extension}&series=${params.id}`
    );
  };

  const handleNumberChange = (newNumber: any) => {
    if (index() === newNumber()) return;
    if (timeoutId) clearTimeout(timeoutId);
    setIsNumberChanging(true);
    timeoutId = setTimeout(() => {
      setIndex(newNumber());
      setIsNumberChanging(false);
      timeoutId = null;
    }, 100);
  };

  createEffect(() => {
    if (seasons()) {
      //@ts-ignore
      setNumber(params.id);
    }
    if (videoPlayer) {
      videoPlayer.destroy();
    }
  });

  // ✅ Enhanced DemoTile with CORS-safe images
  function DemoTile(props) {
    const { imageUrl, isLoading, loadImage } = createCORSSafeImage();

    createEffect(() => {
      if (props.info.movie_image) {
        loadImage(props.info.movie_image, "/assets/television.png");
      }
    });

    return (
      <View
        onEnter={() => onEnterSeries(props.id, props.title, props.container_extension)}
        onFocus={() => {
          setEpsiodeTitle(props.title);
          setEpsiodeDate(props.info.air_date);
          setEpsiodeDuration(props.info.duration);
          setEpsiodeRating(props.info.rating);
        }}
        //@ts-ignore
        states="active"
        style={{
          borderRadius: 10,
          width: 390,
          height: 290,
          scale: 1,
          zIndex: 2,
          color: hexColor("#484e55"),
          transition: { scale: { duration: 250, easing: "ease-in-out" } },
          focus: { scale: 1, color: hexColor("#128DD3") },
        }}
      >
        <View>
          <View
            src="https://i.ibb.co/6R7bqzr3/play-button-1.png"
            width={40}
            height={40}
            zIndex={999}
            x={35}
            y={223}
          />
          <Text x={90} y={230} style={{ fontSize: 28 }} zIndex={999}>
            Episode {props.index}
          </Text>

          {/* Show loading state */}
          <Show when={isLoading()}>
            <View
              style={{
                width: 380,
                height: 280,
                borderRadius: 10,
                x: 5,
                y: 5,
                backgroundColor: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text style={{ color: '#666', fontSize: 12 }}>Loading...</Text>
            </View>
          </Show>

          {/* Show image when loaded */}
          <Show when={!isLoading()}>
            <View
              src={imageUrl()}
              style={{
                width: 380,
                height: 280,
                borderRadius: 10,
                x: 5,
                y: 5
              }}
            />
          </Show>
        </View>
      </View>
    );
  }

  // ✅ CORS-safe star component
  function CORSSafeStar(props: { src: string; x: number; width: number; height: number; index: number }) {
    const { imageUrl, loadImage } = createCORSSafeImage();

    createEffect(() => {
      if (props.src) {
        loadImage(props.src, "/assets/star-placeholder.png");
      }
    });

    return (
      <View
        key={props.index}
        x={props.x}
        width={props.width}
        height={props.height}
        src={imageUrl()}
      />
    );
  }

  // ✅ Enhanced season cover with CORS-safe images
  function SeasonCover(props: { cover: string; name: string; index: number; onEnter: () => void }) {
    const { imageUrl, isLoading, loadImage } = createCORSSafeImage();

    createEffect(() => {
      if (props.cover) {
        loadImage(props.cover, "/assets/placeholder.png");
      }
    });

    return (
      <View
        autofocus={isFirst(props.index)}
        style={styles.moviesBackgroundItem}
        index={props.index}
        //@ts-ignore
        states="active"
        onEnter={props.onEnter}
      >
        {/* Show loading state */}
        <Show when={isLoading()}>
          <View
            x={7}
            y={7}
            borderRadius={10}
            width={244}
            height={390}
            style={{
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text style={{ color: '#666', fontSize: 12 }}>Loading...</Text>
          </View>
        </Show>

        {/* Show image when loaded */}
        <Show when={!isLoading()}>
          <View
            x={7}
            y={7}
            borderRadius={10}
            width={244}
            height={390}
            src={imageUrl()}
          />
        </Show>

        <Text fontSize={24} x={10} y={420}>
          {props.name}
        </Text>
      </View>
    );
  }

  function formatDuration(duration?: string): string {
    if (!duration || !duration.includes(":")) return "";
    const [hours, minutes] = duration.split(":");
    const h = parseInt(hours || "0", 10);
    const m = parseInt(minutes || "0", 10);
    return `${h} h ${m} m`;
  }

  function formatRating(rating?: number | string): string {
    const num = parseFloat(String(rating));
    return !isNaN(num) ? `${num.toFixed(1)} /10` : "";
  }

  function formatReleaseYear(dateStr?: string): string {
    if (!dateStr || !dateStr.includes("-")) return "";
    const year = dateStr.split("-")[0];
    return /^\d{4}$/.test(year) ? year : "";
  }

  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const currentCategoryID = segments.length > 0 ? Number(segments[segments.length - 1]) : null;

  function openEpsiodes() {
    const providers = providerOfSeries().pages();
    if (Array.isArray(providers) && providers.length > 0) {
      setTimeout(() => {
        seriesRow?.setFocus();
      }, 800);
    }
  }

  return (
    <Show when={data()}>
      {/* ✅ Background with proper fallback handling */}
      <View src={backImage() || "/assets/placeholder.png"}>
        <View
          onEscape={() => {
            if (!isEpsiodesView()) onEscape();
          }}
          src="https://i.ibb.co/NrtvBBD/hero-mask-inverted.png"
        >
          <View src="https://i.ibb.co/Lzk5hD4Y/Maskgroup.png">
            {/* ✅ Conditional background color based on image loading success */}
            <View color={shouldUseTransparentBackground() ? hexColor("#00000000") : hexColor("#182027")}>
              <View src="https://i.ibb.co/ZVmDwNg/logo.png" x={800} y={50} width={270} height={82} />
              <View x={50} y={50}>
                <View
                  src="https://i.ibb.co/LX13T2JW/Group-Live-Tv.png"
                  width={102}
                  height={102}
                />
              </View>

              <View onLeft={() => true}>
                <Show
                  when={!isEpsiodesView()}
                  fallback={
                    <View ref={episodesViewRef} onEscape={() => setTimeout(() => setisEpisodesView(false), 300)}>
                      <Show
                        when={providerOfSeries().pages() && !isNumberChanging()}
                        fallback={
                          <View x={480} y={80}>
                            <Text style={{ fontSize: 25 }}>No episodes available</Text>
                          </View>
                        }
                      >
                        <Text fontSize={38} x={100} y={350}>
                          {data().heroContent.title}
                        </Text>

                        <Row ref={seriesRow} plinko announce="Series" x={100} gap={15} selected={0} y={400}>
                          <For each={providerOfSeries().pages()}>
                            {(demo, i) => <DemoTile index={i()} {...demo} />}
                          </For>
                        </Row>

                        <View>
                          <Text fontSize={30} y={750} x={100}>
                            {episodeTitle()}
                          </Text>
                          {epsisodeRating() && (
                            <View y={795} x={100} direction="row">
                              {Array.from({ length: 5 }).map((_, index) => {
                                const rawRating = parseFloat(String(epsisodeRating()));
                                const rating = !isNaN(rawRating) ? rawRating / 2 : 0;
                                const fullStars = Math.floor(rating);
                                const hasHalfStar = rating % 1 >= 0.5;

                                const starSrc =
                                  index < fullStars
                                    ? "https://i.ibb.co/4gK6MnK/star-1.png"
                                    : index === fullStars && hasHalfStar
                                      ? "https://i.ibb.co/9V7LVwc/rating.png"
                                      : "https://i.ibb.co/1KBsPWh/star-2.png";

                                return (
                                  <CORSSafeStar
                                    key={index}
                                    src={starSrc}
                                    x={index * 35}
                                    width={30}
                                    height={30}
                                    index={index}
                                  />
                                );
                              })}
                            </View>
                          )}

                          <View x={200}>
                            <Text y={800} x={100} fontSize={28} color="#b5b1b1">
                              {formatRating(epsisodeRating())}
                            </Text>
                            <Text y={800} x={210} fontSize={28} color="#b5b1b1">
                              | {formatReleaseYear(epsisodeDate())}
                            </Text>
                            <Text y={800} x={300} fontSize={28} color="#b5b1b1">
                              | {formatDuration(epsisodeDuration())}
                            </Text>
                          </View>
                        </View>
                      </Show>
                    </View>
                  }
                >
                  <ContentBlockSeries y={240} x={100} content={data().heroContent} />
                  <Show
                    when={seasons()}
                    fallback={
                      <View x={1000} y={450}>
                        <LoadingIndicator isCategoriesIndicator />
                      </View>
                    }
                  >
                    <View x={100} y={520} clipping>
                      <Row ref={myButton} gap={20} style={RowStyles} y={50} scroll="always">
                        <For each={seasons()}>
                          {(categorie, i) => (
                            <SeasonCover
                              cover={categorie.cover}
                              name={categorie.name}
                              index={i()}
                              onEnter={() => {
                                handleNumberChange(i);
                                setisEpisodesView(true);
                                openEpsiodes();
                              }}
                            />
                          )}
                        </For>
                      </Row>
                    </View>
                  </Show>
                </Show>
                <View ref={backdropRef} style={Backdrop} transition={{ alpha: true, y: true }} />
              </View>
            </View>
          </View>
          <View alpha={0} color={hexColor("#000000")} zIndex={200} transition={{ alpha: true }} />
        </View>
      </View>
    </Show>
  );
};

export default SerieDetails;